import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { driveAPI, pushAPI } from '../utils/api';
import { Calendar as CalendarIcon, Mail, MessageSquare, RefreshCw, Check, X, ExternalLink, HardDrive, Bell, Clock, Plus, Trash2, Save, Activity } from 'lucide-react';
import { toast } from 'sonner';
import NotificationsHealthPanel from '../components/NotificationsHealthPanel';
import { ThemeToggle } from '../contexts/ThemeContext';

const Settings = () => {
  const [searchParams] = useSearchParams();
  const [activeSubTab, setActiveSubTab] = useState(searchParams.get('tab') === 'notifications' ? 'notifications' : 'general');
  const [connecting, setConnecting] = useState(false);
  const [connectingDrive, setConnectingDrive] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [driveStatus, setDriveStatus] = useState(null);
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [editingReminders, setEditingReminders] = useState(false);
  const [reminderDays, setReminderDays] = useState([7, 3, 1]);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [smsRemindersEnabled, setSmsRemindersEnabled] = useState(false);
  const [newReminderDay, setNewReminderDay] = useState('');
  const [savingReminders, setSavingReminders] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [testingPush, setTestingPush] = useState(false);

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      const { data } = await pushAPI.test();
      if (!data.firebase_ready) {
        toast.error('Push is not configured on the server yet.');
      } else if (!data.tokens_found) {
        toast.error('No device registered. Open the app on your phone (logged in, notifications allowed), then try again.');
      } else if (data.sent > 0) {
        toast.success('Test notification sent! Check your phone 🔔');
      } else {
        toast.error(`Could not deliver: ${(data.failures && data.failures[0]) || 'unknown error'}`);
      }
    } catch {
      toast.error('Could not send test notification');
    } finally {
      setTestingPush(false);
    }
  };

  const fetchCalendarStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/oauth/calendar/status');
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    }
  }, []);

  const fetchNotificationConfig = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications/config');
      setNotificationConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch notification config:', error);
    }
  }, []);

  const fetchReminderStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/reminders/status');
      setReminderStatus(response.data);
      setReminderDays(response.data.reminder_days || [7, 3, 1]);
      setRemindersEnabled(response.data.reminders_enabled);
      setSmsRemindersEnabled(response.data.sms_enabled);
    } catch (error) {
      console.error('Failed to fetch reminder status:', error);
    }
  }, []);

  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      await api.post('/api/reminders/trigger');
      toast.success('Reminder check completed!');
      fetchReminderStatus();
    } catch (error) {
      toast.error('Failed to trigger reminders');
    } finally {
      setTriggeringReminders(false);
    }
  };

  const handleRecalculateDeadlines = async () => {
    if (!window.confirm('Recalculate photo/video deadlines for ALL existing workflow events?')) return;
    setRecalculating(true);
    try {
      const res = await api.post('/api/workflow/recalculate-deadlines');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddReminderDay = () => {
    const day = parseInt(newReminderDay);
    if (isNaN(day) || day < 1 || day > 30) {
      toast.error('Please enter a number between 1 and 30');
      return;
    }
    if (reminderDays.includes(day)) {
      toast.error('This day is already added');
      return;
    }
    setReminderDays(prev => [...prev, day].sort((a, b) => b - a));
    setNewReminderDay('');
  };

  const handleRemoveReminderDay = (day) => {
    if (reminderDays.length <= 1) {
      toast.error('You need at least one reminder day');
      return;
    }
    setReminderDays(prev => prev.filter(d => d !== day));
  };

  const handleSaveReminderSettings = async () => {
    setSavingReminders(true);
    try {
      await api.put('/api/reminders/settings', {
        days: reminderDays,
        enabled: remindersEnabled,
        sms_enabled: smsRemindersEnabled
      });
      toast.success('Reminder settings saved!');
      setEditingReminders(false);
      fetchReminderStatus();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleGoogleCalendarConnect = async () => {
    setConnecting(true);
    try {
      const response = await api.get('/api/oauth/calendar/login');
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        toast.error('Google Calendar credentials not configured.');
      }
    } catch (error) {
      toast.error('Failed to connect Google Calendar.');
    } finally {
      setConnecting(false);
    }
  };

  const handleGoogleCalendarDisconnect = async () => {
    try {
      await api.post('/api/oauth/calendar/disconnect');
      toast.success('Google Calendar disconnected');
      fetchCalendarStatus();
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  const handleSyncAllEvents = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/api/calendar/sync-all');
      toast.success(response.data.message);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to sync events');
      }
    } finally {
      setSyncing(false);
    }
  };

  const fetchDriveStatus = useCallback(async () => {
    try {
      const response = await driveAPI.getStatus();
      setDriveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch drive status:', error);
    }
  }, []);

  useEffect(() => {
    fetchCalendarStatus();
    fetchDriveStatus();
    fetchNotificationConfig();
    fetchReminderStatus();
    
    if (searchParams.get('google_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      fetchCalendarStatus();
    } else if (searchParams.get('drive_connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      fetchDriveStatus();
    } else if (searchParams.get('error')) {
      toast.error('Connection failed. Please try again.');
    }
  }, [searchParams, fetchCalendarStatus, fetchDriveStatus, fetchNotificationConfig, fetchReminderStatus]);

  const handleDriveConnect = async () => {
    setConnectingDrive(true);
    try {
      const response = await driveAPI.connect();
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch {
      toast.error('Failed to connect Google Drive.');
    } finally {
      setConnectingDrive(false);
    }
  };

  const handleDriveDisconnect = async () => {
    try {
      await driveAPI.disconnect();
      toast.success('Google Drive disconnected');
      fetchDriveStatus();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-4xl font-serif mb-2">Settings</h1>
          <p className="text-muted-foreground">Configure integrations and preferences</p>
        </div>

        {/* Appearance */}
        <div className="card p-4 sm:p-6 mb-6" data-testid="appearance-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Appearance</h2>
              <p className="text-sm text-muted-foreground">Choose light, dark, or match your device.</p>
            </div>
            <ThemeToggle />
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveSubTab('general')}
            className={`flex items-center gap-1.5 px-4 sm:px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeSubTab === 'general' ? 'border-b-2 border-foreground text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="settings-tab-general"
          >
            <CalendarIcon size={14} /> GENERAL
          </button>
          <button
            onClick={() => setActiveSubTab('notifications')}
            className={`flex items-center gap-1.5 px-4 sm:px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
              activeSubTab === 'notifications' ? 'border-b-2 border-foreground text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="settings-tab-notifications"
          >
            <Activity size={14} /> NOTIFICATIONS
          </button>
        </div>

        {activeSubTab === 'notifications' && <NotificationsHealthPanel />}

        {activeSubTab === 'general' && (
        <div className="space-y-6">
          {/* Google Calendar Integration */}
          <div className="card p-6">
            <h2 className="text-xl font-serif mb-4">Google Calendar</h2>

            <div className="p-4 border border-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">Two-Way Calendar Sync</h3>
                    <p className="text-sm text-muted-foreground">
                      Sync your events with Google Calendar
                    </p>
                  </div>
                </div>
                {calendarStatus?.connected ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-3 py-1 bg-green-100 dark:bg-green-500/15">
                    <Check size={12} />
                    CONNECTED
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-1 bg-muted">
                    NOT CONNECTED
                  </span>
                )}
              </div>

              {calendarStatus?.connected && (
                <div className="mb-4 p-3 bg-muted text-sm">
                  <p><strong>Connected Account:</strong> {calendarStatus.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Connected on {new Date(calendarStatus.connected_at).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                {calendarStatus?.connected ? (
                  <>
                    <button
                      onClick={handleSyncAllEvents}
                      disabled={syncing}
                      className="px-4 py-2 bg-primary text-primary-foreground hover:bg-black/90 transition-colors text-xs flex items-center gap-2"
                      data-testid="sync-google-calendar"
                    >
                      <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                      {syncing ? 'SYNCING...' : 'SYNC ALL EVENTS'}
                    </button>
                    <button
                      onClick={handleGoogleCalendarDisconnect}
                      className="px-4 py-2 border border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 dark:bg-red-500/10 transition-colors text-xs flex items-center gap-2"
                      data-testid="disconnect-google-calendar"
                    >
                      <X size={14} />
                      DISCONNECT
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleGoogleCalendarConnect}
                    disabled={connecting || !calendarStatus?.configured}
                    className="px-4 py-2 bg-primary text-primary-foreground hover:bg-black/90 transition-colors text-xs flex items-center gap-2 disabled:opacity-50"
                    data-testid="connect-google-calendar"
                  >
                    <ExternalLink size={14} />
                    {connecting ? 'CONNECTING...' : 'CONNECT GOOGLE CALENDAR'}
                  </button>
                )}
              </div>

              {!calendarStatus?.configured && (
                <p className="mt-3 text-xs text-red-500">
                  Google Calendar credentials not configured. Please add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET.
                </p>
              )}
            </div>
          </div>

          {/* Google Drive Integration */}
          <div className="card p-6">
            <h2 className="text-xl font-serif mb-4">Google Drive</h2>
            <div className="p-4 border border-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <HardDrive className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">File Storage</h3>
                    <p className="text-sm text-muted-foreground">
                      Store project files, contracts, and documents
                    </p>
                  </div>
                </div>
                {driveStatus?.connected ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-3 py-1 bg-green-100 dark:bg-green-500/15">
                    <Check size={12} />
                    CONNECTED
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-1 bg-muted">
                    NOT CONNECTED
                  </span>
                )}
              </div>

              {driveStatus?.connected && (
                <div className="mb-4 p-3 bg-muted text-sm">
                  <p>Files are stored in <strong>Ami Video</strong> folder on your Google Drive</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Connected on {new Date(driveStatus.connected_at).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                {driveStatus?.connected ? (
                  <button
                    onClick={handleDriveDisconnect}
                    className="px-4 py-2 border border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 dark:bg-red-500/10 transition-colors text-xs flex items-center gap-2"
                    data-testid="disconnect-google-drive"
                  >
                    <X size={14} />
                    DISCONNECT
                  </button>
                ) : (
                  <button
                    onClick={handleDriveConnect}
                    disabled={connectingDrive || !driveStatus?.configured}
                    className="px-4 py-2 bg-primary text-primary-foreground hover:bg-black/90 transition-colors text-xs flex items-center gap-2 disabled:opacity-50"
                    data-testid="connect-google-drive"
                  >
                    <ExternalLink size={14} />
                    {connectingDrive ? 'CONNECTING...' : 'CONNECT GOOGLE DRIVE'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="card p-6">
            <h2 className="text-xl font-serif mb-4">Notifications</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-border">
                <div className="flex items-center gap-3">
                  <Bell className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">Push Notifications</h3>
                    <p className="text-sm text-muted-foreground">Send a test banner to this phone to check push is working</p>
                  </div>
                </div>
                <button
                  onClick={handleTestPush}
                  disabled={testingPush}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-medium transition-all active:scale-95 disabled:opacity-50"
                  data-testid="test-push-button"
                >
                  <Bell size={15} /> {testingPush ? 'Sending…' : 'Send Test'}
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border border-border">
                <div className="flex items-center gap-3">
                  <Mail className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">Email Notifications</h3>
                    <p className="text-sm text-muted-foreground">
                      {notificationConfig?.email?.configured 
                        ? `Sending from ${notificationConfig.email.sender}`
                        : 'Send event details via email'}
                    </p>
                  </div>
                </div>
                {notificationConfig?.email?.configured ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-3 py-1 bg-green-100 dark:bg-green-500/15">
                    <Check size={12} />
                    ACTIVE
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-1 bg-muted">NOT CONFIGURED</span>
                )}
              </div>

              <div className="flex items-center justify-between p-4 border border-border">
                <div className="flex items-center gap-3">
                  <MessageSquare className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">SMS / Text</h3>
                    <p className="text-sm text-muted-foreground">
                      {notificationConfig?.sms?.configured 
                        ? `Sending from ${notificationConfig.sms.from_number}`
                        : 'Send text messages to crew'}
                    </p>
                  </div>
                </div>
                {notificationConfig?.sms?.configured ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-3 py-1 bg-green-100 dark:bg-green-500/15">
                    <Check size={12} />
                    ACTIVE
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-1 bg-muted">NOT CONFIGURED</span>
                )}
              </div>

              <div className="flex items-center justify-between p-4 border border-border">
                <div className="flex items-center gap-3">
                  <MessageSquare className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">WhatsApp</h3>
                    <p className="text-sm text-muted-foreground">
                      {notificationConfig?.whatsapp?.configured 
                        ? `Sending from ${notificationConfig.whatsapp.from_number}`
                        : 'Send messages via WhatsApp'}
                    </p>
                  </div>
                </div>
                {notificationConfig?.whatsapp?.configured ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-3 py-1 bg-green-100 dark:bg-green-500/15">
                    <Check size={12} />
                    ACTIVE
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-1 bg-muted">NOT CONFIGURED</span>
                )}
              </div>
            </div>
          </div>

          {/* Automatic Event Reminders */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif">Automatic Event Reminders</h2>
              {!editingReminders ? (
                <button
                  onClick={() => setEditingReminders(true)}
                  className="text-sm text-gold hover:underline"
                >
                  Edit Settings
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingReminders(false);
                      fetchReminderStatus();
                    }}
                    className="text-sm text-muted-foreground hover:underline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveReminderSettings}
                    disabled={savingReminders}
                    className="flex items-center gap-1 text-sm text-gold hover:underline"
                  >
                    <Save size={14} />
                    {savingReminders ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            
            <div className="p-4 border border-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Bell className="text-gold" size={24} />
                  <div>
                    <h3 className="font-medium">Crew Reminders</h3>
                    <p className="text-sm text-muted-foreground">
                      Automatic email reminders before events
                    </p>
                  </div>
                </div>
                {editingReminders ? (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={remindersEnabled}
                        onChange={(e) => setRemindersEnabled(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm flex items-center gap-1"><Mail size={14} /> Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={smsRemindersEnabled}
                        onChange={(e) => setSmsRemindersEnabled(e.target.checked)}
                        className="w-4 h-4"
                        data-testid="sms-master-toggle"
                      />
                      <span className="text-sm flex items-center gap-1"><MessageSquare size={14} /> SMS (all texts)</span>
                    </label>
                    <p className="text-xs text-muted-foreground sm:w-full">
                      Master switch for all SMS (reminders, payments, assignments). Keep OFF until your Twilio toll-free number is verified — otherwise texts are silently dropped and still billed.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {reminderStatus?.reminders_enabled && (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-2 py-1 bg-green-100 dark:bg-green-500/15">
                        <Mail size={12} /> Email
                      </span>
                    )}
                    {reminderStatus?.sms_enabled && (
                      <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 px-2 py-1 bg-blue-100 dark:bg-blue-500/15">
                        <MessageSquare size={12} /> SMS
                      </span>
                    )}
                    {!reminderStatus?.reminders_enabled && !reminderStatus?.sms_enabled && (
                      <span className="text-xs text-muted-foreground px-3 py-1 bg-muted">DISABLED</span>
                    )}
                  </div>
                )}
              </div>

              {/* Reminder Schedule */}
              <div className="mb-4 p-4 bg-muted/50">
                <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                  <Clock size={14} />
                  Reminder Schedule (days before event)
                </h4>
                
                {editingReminders ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {reminderDays.map(day => (
                        <div key={day} className="flex items-center gap-1 px-3 py-2 bg-background border border-gold">
                          <span className="font-medium text-gold">{day}</span>
                          <span className="text-xs text-muted-foreground">day{day !== 1 ? 's' : ''}</span>
                          <button
                            onClick={() => handleRemoveReminderDay(day)}
                            className="ml-1 text-muted-foreground hover:text-destructive"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={newReminderDay}
                        onChange={(e) => setNewReminderDay(e.target.value)}
                        placeholder="Add days (1-30)"
                        className="w-32 px-3 py-2 border border-input bg-background text-sm"
                      />
                      <button
                        onClick={handleAddReminderDay}
                        className="px-3 py-2 border border-black/15 text-foreground hover:bg-gold/10"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 text-center">
                    {reminderDays.map(day => (
                      <div key={day} className="p-3 bg-background border border-border">
                        <p className="text-2xl font-serif text-gold">{day}</p>
                        <p className="text-xs text-muted-foreground">day{day !== 1 ? 's' : ''} before</p>
                      </div>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground mt-3">
                  Reminders are sent automatically to crew members who have accepted their assignment.
                </p>
              </div>

              {/* Stats */}
              {reminderStatus && (
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div className="p-3 border border-border text-center">
                    <p className="text-2xl font-serif">{reminderStatus.upcoming_events_count}</p>
                    <p className="text-xs text-muted-foreground">Upcoming Events (7 days)</p>
                  </div>
                  <div className="p-3 border border-border text-center">
                    <p className="text-2xl font-serif">{reminderStatus.recent_reminders_sent}</p>
                    <p className="text-xs text-muted-foreground">Reminders Sent</p>
                  </div>
                </div>
              )}

              {/* Recent Reminders */}
              {reminderStatus?.recent_reminders?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium mb-2">Recent Reminders</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {reminderStatus.recent_reminders.map((reminder, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 text-sm">
                        <span>Event: {reminder.event_id?.slice(0, 8)}...</span>
                        <span className="text-muted-foreground">{reminder.days_before} days before</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(reminder.sent_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleTriggerReminders}
                disabled={triggeringReminders || !reminderStatus?.reminders_enabled}
                className="px-4 py-2 bg-primary text-primary-foreground hover:bg-black/90 transition-colors text-xs flex items-center gap-2 disabled:opacity-50"
                data-testid="trigger-reminders"
              >
                <RefreshCw size={14} className={triggeringReminders ? 'animate-spin' : ''} />
                {triggeringReminders ? 'CHECKING...' : 'CHECK & SEND REMINDERS NOW'}
              </button>
            </div>
          </div>

          {/* Workflow Maintenance */}
          <div className="card p-6">
            <h2 className="text-xl font-serif mb-2">Workflow Maintenance</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Recalculates photo / video deadlines for every existing workflow event using the current formula
              (event + 24 days for photos, event + 38 days for videos). Run this once after changing the deadline rules.
            </p>
            <button
              onClick={handleRecalculateDeadlines}
              disabled={recalculating}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 text-sm"
              data-testid="recalculate-deadlines-btn"
            >
              <RefreshCw size={14} className={recalculating ? 'animate-spin' : ''} />
              {recalculating ? 'RECALCULATING...' : 'RECALCULATE ALL WORKFLOW DEADLINES'}
            </button>
          </div>

          {/* App Information */}
          <div className="card p-6">
            <h2 className="text-xl font-serif mb-4">App Information</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Company</span>
                <span>Ami Video</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Version</span>
                <span>1.0.0</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Website</span>
                <a href="https://www.amiphotovideo.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                  amiphotovideo.com
                </a>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
